<script lang="ts">
  import { fade, slide } from "svelte/transition";
  import { get } from "lodash";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import ChartWidget from "@modern/components/retailer/reports/ChartWidget.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import type { ProductDetail, ReportDetailsMonth } from "@shared/types/Reports";

  export let showDetails = null;
  export let productsDetailData: ProductDetail[];
  export let monthsDetailData;
  export let distributorId: number = null;
  export let layoutName: string = "desktop";

  let showProductDetails = {};
  let shouldShowDetails = false;
  $: shouldShowDetails = showDetails ? showDetails["showDetail"] : null;

  const toggleShowProductDetails = (distributorId, productId) => {
    const key = `${distributorId}_${productId}`;
    showProductDetails[key] = !showProductDetails[key];
  };

  const getPLDetail = (distributorId, productDetails) => {
    if (!get(monthsDetailData, [distributorId, productDetails.product_line_id])) {
      HttpPromise({
        url: "/api/retailer/reports/orders_detail_months",
        method: "GET",
        data: {
          distributor_id: distributorId,
          product_line_id: productDetails.product_line_id,
        },
      }).then(
        function success(res: ReportDetailsMonth[]) {
          if (!monthsDetailData[distributorId]) {
            monthsDetailData[distributorId] = {};
          }

          monthsDetailData[distributorId][productDetails.product_line_id] = res;
        },
        function error(res) {
          monthsDetailData[distributorId] = res;
        }
      );
    }
    toggleShowProductDetails(distributorId, productDetails.product_id);
  };

</script>

{#if shouldShowDetails}
  <div transition:slide class="grid-col c12-12 report-order__details">
    <!-- Desktop Product Lines -->
    {#if layoutName === "desktop"}
      <div class="grid-row margin-top--small margin-bottom--smaller">
        <div class="grid-col c3-10 vertical-align-bottom">
          <div class="secondary-small--regular uppercase ellipsis">Product</div>
        </div>
        <div class="grid-col c2-10 vertical-align-bottom">
          <div class="secondary-small--regular uppercase ellipsis">Specifications</div>
        </div>
        <div class="grid-col c2-10 vertical-align-bottom">
          <div class="secondary-small--regular uppercase ellipsis">Times Ordered</div>
        </div>
        <div class="grid-col c1-10 vertical-align-bottom">
          <div class="secondary-small--regular uppercase ellipsis">QTY</div>
        </div>
        <div class="grid-col c2-10 vertical-align-bottom">
          <div class="secondary-small--regular uppercase ellipsis">Spend</div>
        </div>
      </div>
      {#each productsDetailData as productDetails}
        <div
          class="grid-row padding-bottom--small padding-top--small report-order__product-line position-relative"
        >
          <div class="grid-col c3-10 vertical-align-bottom">
            <div class="default-body--bold ellipsis notranslate">{productDetails.name}</div>
          </div>
          <div class="grid-col c2-10 vertical-align-bottom">
            <div class="default-body--regular ellipsis">
              {#if productDetails.container_carton_size > 0}
                <span>
                  {productDetails.container_carton_size} -
                </span>
              {/if}
              {productDetails.container_size}
              {productDetails.container_units}
              {productDetails.container_type}
              {#if productDetails.container_case_size}
                - {productDetails.container_case_size}/case
              {/if}
            </div>
          </div>
          <div class="grid-col c2-10 vertical-align-bottom">
            <div class="default-body--regular ellipsis">{productDetails.times_ordered}</div>
          </div>
          <div class="grid-col c1-10 vertical-align-bottom">
            <div class="default-body--regular ellipsis">{productDetails.quantity}</div>
          </div>
          <div class="grid-col c2-10 vertical-align-bottom">
            <div class="default-body--regular ellipsis">
              <FormatMoney input={productDetails.spend} />
            </div>
          </div>
          <!-- svelte-ignore a11y-missing-attribute -->
          <a
            class="brand-title--bold padding--smaller cursor-pointer report-order__product-line-expand"
            on:click={() => getPLDetail(distributorId, productDetails)}
            data-testid={`hideShow_${distributorId}_${productDetails.product_id}`}
          >
            {showProductDetails[`${distributorId}_${productDetails.product_id}`] ? "-" : "+"}
          </a>
          {#if showProductDetails[`${distributorId}_${productDetails.product_id}`]}
            <div
              class="grid-col c12-12 padding-top--small padding-bottom--small margin-bottom--smaller"
              data-testid={`productLineDetails_${distributorId}_${productDetails.product_id}`}
              transition:fade
            >
              <ChartWidget
                purchaseDetailsByMonth={get(monthsDetailData, [
                  distributorId,
                  productDetails.product_line_id,
                ])}
                {layoutName}
              />
            </div>
          {/if}
        </div>
      {/each}
      <!-- // Desktop Product Lines -->
      <!-- Mobile Product Lines -->
    {:else}
      {#each productsDetailData as productDetails}
        <div
          class="grid-row padding-bottom--small padding-top--small report-order__productDetails-line position-relative"
        >
          <div class="grid-col c10-10 vertical-align-bottom">
            <div class="default-body--bold ellipsis notranslate">{productDetails.name}</div>
          </div>
          <div class="grid-col c10-10 vertical-align-bottom">
            <div class="default-body--regular ellipsis">
              {#if productDetails.container_carton_size > 0}
                <span>
                  {productDetails.container_carton_size} -
                </span>
              {/if}
              {productDetails.container_size}
              {productDetails.container_units}
              {productDetails.container_type} - {productDetails.container_case_size}/case
            </div>
          </div>
          <div class="grid-col c10-10 vertical-align-bottom">
            <div class="default-small--regular ellipsis margin-top--smaller">
              <span class="secondary-small--bold uppercase">Times Ordered:</span>
              {productDetails.times_ordered}
              <span class="secondary-small--bold uppercase padding-left--smaller">QTY:</span>
              {productDetails.quantity}
              <span class="secondary-small--bold uppercase padding-left--smaller"> SPEND:</span>
              <FormatMoney input={productDetails.spend} />
            </div>
          </div>
          <!-- svelte-ignore a11y-missing-attribute -->
          <a
            class="brand-title--bold padding--small cursor-pointer report-order__product-line-expand"
            on:click={() => getPLDetail(distributorId, productDetails)}
            data-testid={`hideShow_${distributorId}_${productDetails.product_id}`}
          >
            {showProductDetails[`${distributorId}_${productDetails.product_id}`] ? "-" : "+"}
          </a>
          {#if showProductDetails[`${distributorId}_${productDetails.product_id}`]}
            <div
              class="grid-col c10-10 padding-top--small padding-bottom--small vertical-align-middle"
              data-testid={`productLineDetails_${distributorId}_${productDetails.product_id}`}
              transition:fade
            >
              <ChartWidget
                purchaseDetailsByMonth={get(monthsDetailData, [
                  distributorId,
                  productDetails.product_line_id,
                ])}
                {layoutName}
              />
            </div>
          {/if}
        </div>
      {/each}
    {/if}
    <!-- // Mobile Product Lines -->
  </div>
{/if}
